/**
 * The event form's Province → City cascade, over `@amzn/awsgcr-form-options` (AWSGCRFormOptions):
 * gcr-mkt-sce's shared snapshot of the UserCenter list
 * (https://account.amazoncloud.cn/config/cn-cities.json). Using the shared package rather than a
 * copy keeps an event's province / city spelled exactly as the other GCR forms and Kunlun's
 * `companyProvince` / `companyCity` spell them — short names, "广东" not "广东省".
 *
 * Stored as two fields because that is what the service has: `province` and `city` are separate,
 * free-form strings on EventWritableFields (AWSECEventHubModel event-types.smithy). Nothing on the
 * wire enforces the list, so events saved before the cascade — or written by another client — can
 * hold a value the list does not have (`city: '顺德'`, a district). Those are kept and shown as
 * they are rather than blanked: dropping a stored value just because the picker cannot offer it
 * would erase data on the next save.
 *
 * Both fields also take free text, for places the list does not have — an event abroad (CES in Las
 * Vegas: `province: '美国'` or `'Nevada'`, `city: 'Las Vegas'`). The list is offered as suggestions,
 * never enforced; {@link cityAfterProvinceChange} only clears a city the list proves cannot belong.
 */
import { PROVINCE_OPTIONS, getCityOptions } from '@amzn/awsgcr-form-options';

export const PROVINCES: readonly string[] = PROVINCE_OPTIONS.map((o) => o.value);

/** Province → its cities, built once: the package exposes only a per-province lookup. */
const CITIES: ReadonlyMap<string, readonly string[]> = new Map(
  PROVINCES.map((p) => [p, getCityOptions(p).map((o) => o.value)]),
);

/** The cities of one province, in the list's order; empty for an unknown or missing province. */
export function citiesOf(province: string | undefined): readonly string[] {
  return (province && CITIES.get(province)) || [];
}

/**
 * The province a city belongs to, or undefined when the list does not have it.
 *
 * For events saved before the form asked for a province: the city alone usually names it. A name
 * shared by two provinces is ambiguous and answers undefined rather than guessing.
 */
export function provinceOfCity(city: string | undefined): string | undefined {
  if (!city) return undefined;
  const matches = PROVINCES.filter((p) => citiesOf(p).includes(city));
  return matches.length === 1 ? matches[0] : undefined;
}

/** What the two selects show when the form opens, from what the event has stored. */
export function initialRegion(
  province: string | undefined,
  city: string | undefined,
): { province: string; city: string } {
  return { province: province || provinceOfCity(city) || '', city: city || '' };
}

/** Whether a city is one the list has, under any province. */
function isListedCity(city: string): boolean {
  return PROVINCES.some((p) => citiesOf(p).includes(city));
}

/**
 * The city to keep when the province changes. A listed city that is not in the new province is
 * cleared — a city left over from the old province would save a pair that cannot exist. A typed city
 * the list does not know (Las Vegas) is kept: nothing says it is wrong.
 */
export function cityAfterProvinceChange(nextProvince: string, city: string): string {
  if (!city || citiesOf(nextProvince).includes(city)) return city;
  return isListedCity(city) ? '' : city;
}
